import React, { useState, useEffect } from 'react'
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  X, 
  Save, 
  Search, 
  Phone, 
  Mail, 
  MapPin,
  User,
  Building,
  Upload,
  Download
} from 'lucide-react'

interface StaffMember {
  id: string
  name: string
  title: string
  department: string
  email: string
  phone: string
  officePhone: string
  extension?: string
  image?: string
}

interface StaffDirectoryProps {
  onClose: () => void
}

const StaffDirectory: React.FC<StaffDirectoryProps> = ({ onClose }) => {
  const [staff, setStaff] = useState<StaffMember[]>([])
  const [showForm, setShowForm] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState('all')
  const [formData, setFormData] = useState<Partial<StaffMember>>({
    name: '',
    title: '',
    department: '',
    email: '',
    phone: '',
    officePhone: '',
    extension: ''
  })

  // Load staff from localStorage on component mount
  useEffect(() => {
    const savedStaff = localStorage.getItem('grace-staff-directory')
    if (savedStaff) {
      try {
        setStaff(JSON.parse(savedStaff))
      } catch (error) {
        console.error('Error loading staff directory:', error)
        // Set default staff if loading fails
        setStaff([
          {
            id: '1',
            name: 'Jeremy Silbernagel',
            title: 'IT Director',
            department: 'Technology',
            email: 'jeremy@gracefm.org',
            phone: '(903) 629-1234',
            officePhone: '(903) 629-1234',
            extension: '101'
          },
          {
            id: '2',
            name: 'Senior Pastor',
            title: 'Lead Pastor',
            department: 'Leadership',
            email: 'pastor@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '100'
          },
          {
            id: '3',
            name: 'Associate Pastor',
            title: 'Associate Pastor',
            department: 'Leadership',
            email: 'associate@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '103'
          },
          {
            id: '4',
            name: 'Worship Pastor',
            title: 'Worship & Music Director',
            department: 'Worship',
            email: 'worship@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '104'
          },
          {
            id: '5',
            name: 'Youth Pastor',
            title: 'Youth Ministry Director',
            department: 'Youth Ministry',
            email: 'youth@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '105'
          },
          {
            id: '6',
            name: 'Children\'s Director',
            title: 'Children\'s Ministry Director',
            department: 'Children\'s Ministry',
            email: 'children@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '106'
          },
          {
            id: '7',
            name: 'Administrative Assistant',
            title: 'Church Administrator',
            department: 'Administration',
            email: 'admin@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '107'
          },
          {
            id: '8',
            name: 'Facilities Manager',
            title: 'Facilities & Maintenance Director',
            department: 'Facilities',
            email: 'facilities@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '108'
          },
          {
            id: '9',
            name: 'Missions Coordinator',
            title: 'Missions & Outreach Director',
            department: 'Missions',
            email: 'missions@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '109'
          },
          {
            id: '10',
            name: 'Connect Groups Pastor',
            title: 'Small Groups & Discipleship Pastor',
            department: 'Connect Groups',
            email: 'connectgroups@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '110'
          },
          {
            id: '11',
            name: 'Financial Secretary',
            title: 'Finance & Accounting',
            department: 'Finance',
            email: 'finance@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '111'
          },
          {
            id: '12',
            name: 'Communications Director',
            title: 'Marketing & Communications',
            department: 'Communications',
            email: 'communications@gracefm.org',
            phone: '(903) 342-6810',
            officePhone: '(903) 342-6810',
            extension: '112'
          }
        ])
      }
    } else {
      // Set default staff if none exist
      setStaff([
        {
          id: '1',
          name: 'Jeremy Silbernagel',
          title: 'IT Director',
          department: 'Technology',
          email: 'jeremy@gracefm.org',
          phone: '(903) 629-1234',
          officePhone: '(903) 629-1234',
          extension: '101'
        },
        {
          id: '2',
          name: 'Senior Pastor',
          title: 'Lead Pastor',
          department: 'Leadership',
          email: 'pastor@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '100'
        },
        {
          id: '3',
          name: 'Associate Pastor',
          title: 'Associate Pastor',
          department: 'Leadership',
          email: 'associate@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '103'
        },
        {
          id: '4',
          name: 'Worship Pastor',
          title: 'Worship & Music Director',
          department: 'Worship',
          email: 'worship@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '104'
        },
        {
          id: '5',
          name: 'Youth Pastor',
          title: 'Youth Ministry Director',
          department: 'Youth Ministry',
          email: 'youth@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '105'
        },
        {
          id: '6',
          name: 'Children\'s Director',
          title: 'Children\'s Ministry Director',
          department: 'Children\'s Ministry',
          email: 'children@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '106'
        },
        {
          id: '7',
          name: 'Administrative Assistant',
          title: 'Church Administrator',
          department: 'Administration',
          email: 'admin@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '107'
        },
        {
          id: '8',
          name: 'Facilities Manager',
          title: 'Facilities & Maintenance Director',
          department: 'Facilities',
          email: 'facilities@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '108'
        },
        {
          id: '9',
          name: 'Missions Coordinator',
          title: 'Missions & Outreach Director',
          department: 'Missions',
          email: 'missions@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '109'
        },
        {
          id: '10',
          name: 'Connect Groups Pastor',
          title: 'Small Groups & Discipleship Pastor',
          department: 'Connect Groups',
          email: 'connectgroups@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '110'
        },
        {
          id: '11',
          name: 'Financial Secretary',
          title: 'Finance & Accounting',
          department: 'Finance',
          email: 'finance@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '111'
        },
        {
          id: '12',
          name: 'Communications Director',
          title: 'Marketing & Communications',
          department: 'Communications',
          email: 'communications@gracefm.org',
          phone: '(903) 342-6810',
          officePhone: '(903) 342-6810',
          extension: '112'
        }
      ])
    }
  }, [])

  // Save staff to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('grace-staff-directory', JSON.stringify(staff))
  }, [staff])

  const resetForm = () => {
    setFormData({
      name: '',
      title: '',
      department: '',
      email: '',
      phone: '',
      officePhone: '',
      extension: ''
    })
    setEditingId(null)
    setShowForm(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name || !formData.title || !formData.department || !formData.email) {
      alert('Please fill in all required fields')
      return
    }

    const newStaffMember: StaffMember = {
      id: editingId || Date.now().toString(),
      name: formData.name,
      title: formData.title,
      department: formData.department,
      email: formData.email,
      phone: formData.phone || '',
      officePhone: formData.officePhone || '',
      extension: formData.extension || ''
    }

    let updatedStaff: StaffMember[]
    
    if (editingId) {
      // Update existing staff member
      updatedStaff = staff.map(member =>
        member.id === editingId ? newStaffMember : member
      )
    } else {
      // Add new staff member
      updatedStaff = [...staff, newStaffMember]
    }

    setStaff(updatedStaff)
    resetForm()
  }

  const handleEdit = (member: StaffMember) => {
    setEditingId(member.id)
    setFormData({
      name: member.name,
      title: member.title,
      department: member.department,
      email: member.email,
      phone: member.phone,
      officePhone: member.officePhone,
      extension: member.extension
    })
    setShowForm(true)
  }

  const handleDelete = (id: string) => {
    if (confirm('Are you sure you want to delete this staff member?')) {
      const updatedStaff = staff.filter(member => member.id !== id)
      setStaff(updatedStaff)
    }
  }

  const handleCSVUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (e) => {
      const text = e.target?.result as string
      const lines = text.split('\n')
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
      
      const newStaff: StaffMember[] = []
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue
        
        const values = line.split(',').map(v => v.trim())
        if (values.length < 4) continue // Need at least name, title, department, email
        
        const staffMember: StaffMember = {
          id: Date.now().toString() + i,
          name: values[headers.indexOf('name')] || values[0] || '',
          title: values[headers.indexOf('title')] || values[1] || '',
          department: values[headers.indexOf('department')] || values[2] || '',
          email: values[headers.indexOf('email')] || values[3] || '',
          phone: values[headers.indexOf('phone')] || values[headers.indexOf('mobile phone')] || '',
          officePhone: values[headers.indexOf('office phone')] || values[headers.indexOf('officephone')] || '',
          extension: values[headers.indexOf('extension')] || values[headers.indexOf('ext')] || ''
        }
        
        if (staffMember.name && staffMember.title && staffMember.department && staffMember.email) {
          newStaff.push(staffMember)
        }
      }
      
      if (newStaff.length > 0) {
        setStaff(prev => [...prev, ...newStaff])
        alert(`Successfully imported ${newStaff.length} staff members!`)
      } else {
        alert('No valid staff members found in the CSV file.')
      }
      
      setShowUploadModal(false)
      // Reset the file input
      event.target.value = ''
    }
    
    reader.readAsText(file)
  }

  const downloadCSVTemplate = () => {
    const headers = ['Name', 'Title', 'Department', 'Email', 'Mobile Phone', 'Office Phone', 'Extension']
    const template = headers.join(',') + '\n' +
      'John Doe,Pastor,Leadership,pastor@gracefm.org,(903) 629-1234,(903) 629-1234,101\n' +
      'Jane Smith,Administrator,Administration,admin@gracefm.org,(903) 629-1235,(903) 629-1235,102'
    
    const blob = new Blob([template], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'staff_directory_template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  // Filter staff based on search term and department
  const filteredStaff = staff.filter(member => {
    const matchesSearch = member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         member.department.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesDepartment = selectedDepartment === 'all' || member.department === selectedDepartment
    return matchesSearch && matchesDepartment
  })

  // Get unique departments for filter
  const departments = ['all', ...Array.from(new Set(staff.map(member => member.department)))]

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Users className="w-6 h-6 text-gray-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Staff Directory</h2>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowForm(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Staff Member
            </button>
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn-secondary flex items-center"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </button>
            <button
              onClick={downloadCSVTemplate}
              className="btn-secondary flex items-center"
            >
              <Download className="w-4 h-4 mr-2" />
              Template
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by name, title, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="md:w-48">
              <select
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
              >
                {departments.map(dept => (
                  <option key={dept} value={dept}>
                    {dept === 'all' ? 'All Departments' : dept}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Staff List */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0">
          {filteredStaff.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No staff members found</h3>
              <p className="text-gray-500">Try adjusting your search or add a new staff member.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStaff.map((member) => (
                <div key={member.id} className="card p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-grace-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-grace-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{member.name}</h3>
                        <p className="text-sm text-gray-600">{member.title}</p>
                        <span className="inline-block mt-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                          {member.department}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(member)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(member.id)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <Mail className="w-4 h-4 mr-2 text-gray-400" />
                      <a href={`mailto:${member.email}`} className="hover:text-grace-blue-600">
                        {member.email}
                      </a>
                    </div>
                    {member.phone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`tel:${member.phone}`} className="hover:text-grace-blue-600">
                          {member.phone}
                        </a>
                      </div>
                    )}
                    {member.officePhone && (
                      <div className="flex items-center text-sm text-gray-600">
                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                        <a href={`tel:${member.officePhone}`} className="hover:text-grace-blue-600">
                          {member.officePhone}
                          {member.extension && ` ext. ${member.extension}`}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingId ? 'Edit Staff Member' : 'Add New Staff Member'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="Full name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="Job title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Department *
                    </label>
                    <input
                      type="text"
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="Department"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="email@gracefm.org"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mobile Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="(903) 629-1234"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Office Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.officePhone}
                      onChange={(e) => setFormData({ ...formData, officePhone: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="(903) 629-1234"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Extension
                    </label>
                    <input
                      type="text"
                      value={formData.extension}
                      onChange={(e) => setFormData({ ...formData, extension: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-grace-blue-500 focus:border-transparent"
                      placeholder="101"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary flex items-center"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {editingId ? 'Update' : 'Add'} Staff Member
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* CSV Upload Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Import Staff from CSV</h3>
                <button
                  onClick={() => setShowUploadModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a CSV file with staff information. The file should include columns for:
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4">
                    <li>• <strong>Name</strong> (required)</li>
                    <li>• <strong>Title</strong> (required)</li>
                    <li>• <strong>Department</strong> (required)</li>
                    <li>• <strong>Email</strong> (required)</li>
                    <li>• <strong>Mobile Phone</strong> (optional)</li>
                    <li>• <strong>Office Phone</strong> (optional)</li>
                    <li>• <strong>Extension</strong> (optional)</li>
                  </ul>
                  <button
                    onClick={downloadCSVTemplate}
                    className="text-sm text-grace-blue-600 hover:text-grace-blue-700 underline"
                  >
                    Download CSV template
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-600 mb-2">Choose a CSV file or drag it here</p>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                      id="csv-upload"
                    />
                    <label
                      htmlFor="csv-upload"
                      className="btn-primary cursor-pointer inline-flex items-center"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Select CSV File
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default StaffDirectory 